import useRequest from "@/lib/request/useRequest";
import { Button, Card, CardBody, Chip, Spinner } from "@heroui/react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { FieldRecommendation } from "@/types/field";
import { WaterRiskZones } from "@/types/water";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import useAuth from "@/lib/auth/useAuth";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { faEdit, faLeaf } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ApplyNChip from "../common/ApplyNChip";
import UserRecommendationForm from "./UserRecommendationForm";
import DoNotApplyNChip from "../common/DoNotApplyNChip";
import IrrigateChip from "../common/IrrigateChip";
import DoNotIrrigateChip from "../common/DoNotIrrigateChip";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { Link, useSearch } from "@tanstack/react-router";

function RecommendationCard() {
    const { advisorEnabled } = useFeatureFlag();
    const { advisor } = useSearch({
        strict: false,
    });
    const { isConsultantOrSentinel } = useAuth();
    const { useGetRequest } = useRequest();
    const { fieldId, fieldQuery, fieldHasIrrigation } =
        useFieldDashboard() ?? {};
    const { customRecommendationsEnabled } = useFeatureFlag();
    const { t } = useTranslation(["common", "messages"]);
    const { formatDateMMDDYYYY } = useI18n();
    const today = getTodayWithTimeTravel();

    const recommendation = useGetRequest<FieldRecommendation>({
        url: `fields/${fieldId}/recommendation/`,
    });

    const userRecommendationContent = useMemo(() => {
        if (
            recommendation.data?.user_recommendation != null &&
            recommendation.data?.user_recommendation.recommend_application !=
                null
        ) {
            return recommendation.data?.user_recommendation
                .recommend_application ? (
                <ApplyNChip />
            ) : (
                <DoNotApplyNChip />
            );
        }
        return null;
    }, [recommendation.data?.user_recommendation]);

    const platformRecommendationContent = useMemo(() => {
        if (recommendation.data?.gap) {
            return (
                <Chip
                    classNames={{
                        base: "bg-gray-100",
                        content: "text-gray-900 font-bold",
                    }}
                    size="sm"
                    radius="sm"
                >
                    {t("image_gap", { ns: "fields-list" })}
                </Chip>
            );
        }

        if (recommendation.data?.recent_application) {
            return (
                <Chip
                    classNames={{
                        base: "bg-green-100 font-bold",
                        content: "font-bold text-green-700",
                    }}
                    radius="sm"
                    size="sm"
                >
                    {t("do_not_apply_n", { ns: "fields-list" })}
                </Chip>
            );
        }

        if (recommendation.data?.recommend_fertigation) {
            return <ApplyNChip />;
        }

        return <DoNotApplyNChip />;
    }, [
        recommendation.data?.gap,
        recommendation.data?.recent_application,
        recommendation.data?.recommend_fertigation,
        t,
    ]);

    const recDate = useMemo(() => {
        if (recommendation.data?.user_recommendation?.recommendation_date) {
            return formatDateMMDDYYYY(
                recommendation.data?.user_recommendation.recommendation_date,
            );
        }

        const d = dayjs(recommendation.data?.recommendation_date);
        if (d.isValid()) {
            return formatDateMMDDYYYY(recommendation.data?.recommendation_date);
        }
        return formatDateMMDDYYYY(dayjs(today));
    }, [
        recommendation.data?.user_recommendation?.recommendation_date,
        recommendation.data?.recommendation_date,
        formatDateMMDDYYYY,
        today,
    ]);

    const [showEditRecommendation, setShowEditRecommendation] = useState(false);

    const fieldSoilWaterContentQuery = useGetRequest<WaterRiskZones>({
        url: `fields/${fieldId}/water_risk_zones/`,
        enabled: !!fieldHasIrrigation,
    });

    const platformIrrigationRecommendationContent = useMemo(() => {
        return fieldSoilWaterContentQuery.data?.irrigation_recommendation ===
            true ? (
            <IrrigateChip />
        ) : fieldSoilWaterContentQuery.data?.irrigation_recommendation ===
          false ? (
            <DoNotIrrigateChip />
        ) : (
            <Chip
                variant="flat"
                color={
                    fieldSoilWaterContentQuery.isLoading ? "default" : "warning"
                }
                radius="sm"
                size="sm"
            >
                {fieldSoilWaterContentQuery.isLoading ? (
                    <Spinner size="sm" />
                ) : (
                    t("no_data", { ns: "common" })
                )}
            </Chip>
        );
    }, [
        t,
        fieldSoilWaterContentQuery.isLoading,
        fieldSoilWaterContentQuery.data?.irrigation_recommendation,
    ]);

    const userIrrigationRecommendationContent = useMemo(() => {
        if (
            recommendation.data?.user_recommendation != null &&
            recommendation.data?.user_recommendation.recommend_irrigation !=
                null
        ) {
            return recommendation.data?.user_recommendation
                .recommend_irrigation ? (
                <IrrigateChip />
            ) : (
                <DoNotIrrigateChip />
            );
        }
        return null;
    }, [recommendation.data?.user_recommendation]);

    if (!fieldQuery?.data?.recommendation?.insights_active) {
        return null;
    }

    return (
        <div>
            <Card className="mx-2">
                <CardBody className="p-[0.4rem]">
                    <div className="flex flex-col gap-[0.15rem]">
                        <div className="flex flex-row justify-between items-center">
                            <div className="col-span-2">
                                <span className="font-bold">Date:</span>{" "}
                                {recDate}
                            </div>
                            {recommendation.data &&
                                customRecommendationsEnabled &&
                                isConsultantOrSentinel && (
                                    <div className="flex flex-row gap-2 items-center h-[24px] ">
                                        {advisorEnabled &&
                                            isConsultantOrSentinel && (
                                                <Button
                                                    variant="flat"
                                                    size="sm"
                                                    color="primary"
                                                    aria-label="Nitrogen Advisor"
                                                    as={Link}
                                                    to="/dashboard/fields/$fieldId/insights"
                                                    search={{
                                                        // @ts-expect-error - Button -> Link
                                                        advisor: !advisor
                                                            ? true
                                                            : undefined,
                                                    }}
                                                    params={{
                                                        // @ts-expect-error - Button -> Link
                                                        fieldId: fieldId,
                                                    }}
                                                    startContent={
                                                        <FontAwesomeIcon
                                                            icon={faLeaf}
                                                        />
                                                    }
                                                >
                                                    Advisor
                                                </Button>
                                            )}
                                        {recommendation.data &&
                                            customRecommendationsEnabled &&
                                            isConsultantOrSentinel && (
                                                <>
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        startContent={
                                                            <FontAwesomeIcon
                                                                icon={faEdit}
                                                            />
                                                        }
                                                        onPress={() =>
                                                            setShowEditRecommendation(
                                                                true,
                                                            )
                                                        }
                                                    >
                                                        Edit
                                                    </Button>
                                                    {showEditRecommendation && (
                                                        <UserRecommendationForm
                                                            fieldIds={[
                                                                fieldId!,
                                                            ]}
                                                            onClose={(
                                                                refetch,
                                                            ) => {
                                                                setShowEditRecommendation(
                                                                    false,
                                                                );
                                                                if (refetch) {
                                                                    recommendation.refetch();
                                                                }
                                                            }}
                                                            platformRecommendationContent={
                                                                platformRecommendationContent
                                                            }
                                                            recommendation={
                                                                recommendation.data
                                                            }
                                                        />
                                                    )}
                                                </>
                                            )}
                                    </div>
                                )}
                        </div>
                        <div className="gap-2 flex flex-row items-center">
                            <span className="font-bold">
                                {t("recommendation", { ns: "insights" })}:{" "}
                            </span>
                            <span className="gap-2 flex flex-row">
                                {userRecommendationContent ??
                                    platformRecommendationContent}
                                {fieldHasIrrigation &&
                                    (userIrrigationRecommendationContent ??
                                        platformIrrigationRecommendationContent)}
                            </span>
                            {(recommendation?.data?.user_recommendation
                                ?.recommend_application !== null ||
                                recommendation?.data?.user_recommendation
                                    ?.recommend_irrigation) &&
                                !!recommendation?.data?.user_recommendation
                                    ?.user && (
                                    <span className="text-xs text-gray-500">
                                        {" "}
                                        recommended by{" "}
                                        {
                                            recommendation?.data
                                                ?.user_recommendation.user
                                        }
                                    </span>
                                )}
                        </div>
                        {recommendation.data?.user_recommendation?.notes && (
                            <div>
                                <span className="font-bold">Notes: </span>
                                <span className="text-sm">
                                    {
                                        recommendation.data?.user_recommendation
                                            .notes
                                    }
                                </span>
                                <span className="text-xs text-gray-500">
                                    {" "}
                                    -{" "}
                                    {
                                        recommendation?.data
                                            ?.user_recommendation.user
                                    }
                                </span>
                            </div>
                        )}
                        {recommendation.data?.gap && (
                            <div className="text-red-500 text-xs">
                                {t("qualityImageryNotReceivedSince", {
                                    ns: "insights",
                                })}{" "}
                                {formatDateMMDDYYYY(
                                    recommendation.data?.recommendation_date,
                                )}
                            </div>
                        )}
                        {recommendation.data?.recent_application &&
                            !recommendation.data?.gap && (
                                <div className="text-red-500 text-xs">
                                    {t("recentApplicationOn", {
                                        ns: "insights",
                                    })}{" "}
                                    {formatDateMMDDYYYY(
                                        recommendation.data?.recent_application,
                                    )}
                                </div>
                            )}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}

export default RecommendationCard;
